import { MapViewport } from "@store/map/mapReducer";
import { useMap } from "@vis.gl/react-google-maps";
import { useEffect, useState } from "react";

type MapViewportOptions = {
  padding?: { left?: number; right?: number; top?: number; bottom?: number };
};

/**
 * Get the current viewport, accepts padding so that overlays can be accounted for so that the
 * visible area is returned.
 *
 * Based on https://github.com/visgl/react-google-maps/blob/main/examples/custom-marker-clustering/src/hooks/use-map-viewport.ts
 */
export function useMapViewport({
  padding = { left: 0, right: 0, top: 0, bottom: 0 },
}: MapViewportOptions = {}) {
  const map = useMap();
  const [viewport, setViewport] = useState<MapViewport>({
    bbox: undefined,
    center: undefined,
    zoom: undefined,
  });

  // observe the map to get current bounds
  useEffect(() => {
    if (!map) {
      return;
    }

    const listener = map.addListener("bounds_changed", () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      const projection = map.getProjection();

      if (!bounds || !zoom || !projection) {
        return;
      }

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      const n = Math.min(
        90,
        ne.lat() - degreesPerPixel(zoom) * (padding.top ?? 0),
      );
      const s = Math.max(
        -90,
        sw.lat() + degreesPerPixel(zoom) * (padding.bottom ?? 0),
      );

      const w = sw.lng() + degreesPerPixel(zoom) * (padding.left ?? 0);
      const e = ne.lng() - degreesPerPixel(zoom) * (padding.right ?? 0);

      if (
        viewport.bbox === undefined ||
        w !== viewport.bbox[0] ||
        s !== viewport.bbox[1] ||
        e !== viewport.bbox[2] ||
        n !== viewport.bbox[3]
      ) {
        setViewport({
          bbox: [w, s, e, n],
          center: [sw.lng(), sw.lat()],
          zoom,
        });
      }
    });

    return () => {
      listener.remove();
    };
  }, [map, padding, viewport.bbox]);

  return viewport;
}

function degreesPerPixel(zoomLevel: number) {
  // 360° divided by the number of pixels at the zoom-level
  return 360 / (Math.pow(2, zoomLevel) * 256);
}
